import styled from "styled-components";
import workPolicyApplicationApi from "../../../../api/work-policy-application-api";
import type { WorkPolicyApplicationApi } from "../../../../api/work-policy-application-api.types";
import { calendarMonthRangeInputBounds } from "../../../work-type-shared/calendar-date-range";
import { WorkPolicySummary } from "../../../work-type-shared/work-policy-summary";
import { EligiblePolicySelect } from "./eligible-policy-select";
import { FlexibleApplicationFields } from "./flexible-application-fields";
import { SelectiveApplicationPanel } from "./selective-application-panel";
import { StaggeredApplicationFields } from "./staggered-application-fields";
import { WorkPolicyApplicationPanel } from "./work-policy-application-panel";
import {
  useWorkPolicyApplicationFormModel,
  WorkPolicyApplicationDateField,
  WorkPolicyApplicationFormModel
} from "./use-work-policy-application-form-model";
import type { WorkPolicyApplicationController } from "./use-work-policy-application-controller";
import { WorkPolicySaveRecovery } from "./work-policy-save-recovery";

interface WorkPolicyApplicationFormProps {
  startDate: WorkPolicyApplicationDateField;
  endDate: WorkPolicyApplicationDateField;
  controller: WorkPolicyApplicationController;
  api?: WorkPolicyApplicationApi;
}

const Section = styled.section`
  grid-column: span 3;
  display: grid;
  gap: 14px;
  min-width: 0;
  padding: 14px;
  border-top: 1px solid ${props => props.theme.colors.grey};
  border-bottom: 1px solid ${props => props.theme.colors.grey};
`;

const DateFields = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(180px, 1fr));
  gap: 12px;

  label {
    display: grid;
    gap: 6px;
  }
`;

const ErrorSummary = styled.div`
  padding: 12px;
  border: 1px solid ${props => props.theme.colors.darkRed};
`;

function unavailablePolicySummary(model: WorkPolicyApplicationFormModel) {
  const draft = model.controller.draft;
  const current = draft?.policySnapshot;
  if (!draft || !current) return null;
  return (
    <section aria-labelledby="saved-policy-title">
      <h3 id="saved-policy-title">저장된 근무제 정책</h3>
      <WorkPolicySummary
        variant="snapshot"
        selected
        policy={{
          id: String(draft.workPolicyId),
          name: current.profileName,
          policyKind: "APPROVAL",
          workType: draft.workType,
          status: "INACTIVE",
          ruleSummary: `일 ${current.dailyBaseHours}시간 · 주 ${current.weeklyBaseHours}시간`
        }}
      />
      <p>현재 선택 목록에는 없지만 임시저장 당시 정책 정보를 유지합니다.</p>
    </section>
  );
}

function validationIssueLabel(
  model: WorkPolicyApplicationFormModel,
  path: string
) {
  if (path === "startDate") return "신청 시작일";
  if (path === "endDate") return "신청 종료일";
  if (path === "selectedStartTime") return "근무 시간대";
  if (path === "settlementPeriods") return "월별 정산구간";
  if (path === "schedule") return "근무 일정";

  const match = /^schedule\[(\d+)\](?:\.(.+))?$/.exec(path);
  const draft = model.controller.draft;
  if (!match || draft?.workType !== "flexible") return "신청 내용";
  const row = draft.schedule[Number(match[1])];
  const rowLabel =
    row?.workDate ??
    (row?.weekdayNo
      ? ["월요일", "화요일", "수요일", "목요일", "금요일", "토요일", "일요일"][
          row.weekdayNo - 1
        ]
      : "근무 일정");
  const fieldLabel: Record<string, string> = {
    startTime: "출근시간",
    plannedWorkMinutes: "계획근로시간",
    breakWaiver: "휴게 미사용",
    workday: "근무일 여부",
    workDate: "근무일",
    weekdayNo: "요일"
  };
  return match[2] && fieldLabel[match[2]]
    ? `${rowLabel} · ${fieldLabel[match[2]]}`
    : `${rowLabel} 일정`;
}

function validationIssueMessage(path: string, message: string) {
  if (!/^(?:A |Must |Only |Rows |DAILY_DATE |WEEKLY_PATTERN )/.test(message)) {
    return message;
  }
  if (path === "startDate") return "신청 시작일을 확인해 주세요.";
  if (path === "endDate") return "신청 종료일을 확인해 주세요.";
  if (path === "selectedStartTime") return "허용된 근무 시간대를 선택해 주세요.";
  if (path === "settlementPeriods") return message;
  if (path.includes("plannedWorkMinutes")) return "계획근로시간을 확인해 주세요.";
  if (path.includes("startTime")) return "출근시간을 확인해 주세요.";
  if (path.includes("breakWaiver")) return "휴게 미사용 조건을 확인해 주세요.";
  if (path.startsWith("schedule")) return "근무 일정을 확인해 주세요.";
  return "신청 내용을 확인해 주세요.";
}

export function workPolicyDetailTitle(model: WorkPolicyApplicationFormModel) {
  const draft = model.controller.draft;
  if (draft?.workType === "staggered") return "시차 출퇴근";
  if (draft?.workType === "selective") return "선택근무";
  if (draft?.workType === "flexible") {
    return draft.flexibleMode === "DAILY_DATE"
      ? "탄력근무 날짜별"
      : "탄력근무 주간";
  }
  return "신청 상세";
}

export function WorkPolicyApplicationDetail({
  model
}: {
  model: WorkPolicyApplicationFormModel;
}) {
  const controller = model.controller;
  const draft = controller.draft;
  const eligibleReady = model.eligibleStatus === "ready";
  const panelTitle = workPolicyDetailTitle(model);
  const policyName =
    model.selectedPolicy?.profileName ?? draft?.policySnapshot?.profileName ?? "저장된 정책";
  return (
    <>
      {eligibleReady && draft && !model.selectedPolicy && unavailablePolicySummary(model)}
      {eligibleReady && !draft && !controller.unsupportedHistorical && (
        <p>
          근무제 유형을 선택하면 정책에 맞는 신청 입력 화면 하나가 표시됩니다.
        </p>
      )}
      {eligibleReady && draft?.workType === "staggered" && (
        <WorkPolicyApplicationPanel
          icon="clock"
          title={panelTitle}
          description="허용된 출퇴근 시간 중 하나를 선택해 신청기간 전체에 적용합니다."
          modeLabel="시차 출퇴근"
          policyName={policyName}
          startDate={draft.startDate}
          endDate={draft.endDate}
        >
          <div id="work-policy-selectedStartTime" tabIndex={-1}>
            <StaggeredApplicationFields
              status={model.staggeredStatus}
              options={model.staggeredOptions}
              value={draft.selectedStartTime}
              onChange={model.updateStaggeredStartTime}
              onRetry={model.retryStaggered}
            />
          </div>
        </WorkPolicyApplicationPanel>
      )}
      {eligibleReady && draft?.workType === "flexible" && (
        <WorkPolicyApplicationPanel
          icon={draft.flexibleMode === "DAILY_DATE" ? "calendar" : "week"}
          title={panelTitle}
          description={
            draft.flexibleMode === "DAILY_DATE"
              ? "신청기간의 각 날짜에 맞춰 근무·휴무 일정을 작성합니다."
              : "월요일부터 일요일까지 작성한 일정이 신청기간 동안 반복됩니다."
          }
          modeLabel={draft.flexibleMode === "DAILY_DATE" ? "날짜별 일정" : "주간 반복"}
          policyName={policyName}
          startDate={draft.startDate}
          endDate={draft.endDate}
        >
          <div id="work-policy-schedule" tabIndex={-1}>
            <FlexibleApplicationFields
              mode={draft.flexibleMode}
              policySnapshot={draft.policySnapshot}
              value={draft.schedule}
              onChange={model.updateFlexibleSchedule}
            />
          </div>
        </WorkPolicyApplicationPanel>
      )}
      {eligibleReady && draft?.workType === "selective" && (
        <SelectiveApplicationPanel
          policyName={policyName}
          startDate={draft.startDate}
          endDate={draft.endDate}
          settlementStartDay={
            Number(
              (
                draft.settlementPeriods[0]?.settlementStartDate ?? draft.startDate
              ).slice(-2)
            ) || null
          }
          snapshot={draft.policySnapshot ?? {
            selectiveCoreEnabled: false,
            selectiveCoreStart: null,
            selectiveCoreEnd: null
          }}
          status={draft.settlementStatus}
          periods={draft.settlementPeriods}
          onRetry={model.retrySettlement}
        />
      )}
      {eligibleReady && controller.validationIssues.length > 0 && (
        <ErrorSummary role="alert" aria-labelledby="work-policy-error-title">
          <strong id="work-policy-error-title">
            입력 오류 {controller.validationIssues.length}건을 확인해 주세요.
          </strong>
          <ul>
            {controller.validationIssues.map(issue => (
              <li key={`${issue.path}-${issue.message}`}>
                {validationIssueLabel(model, issue.path)}: {validationIssueMessage(issue.path, issue.message)}
              </li>
            ))}
          </ul>
        </ErrorSummary>
      )}
      <WorkPolicySaveRecovery
        status={controller.status}
        approvalRequestIdx={controller.approvalRequestIdx}
        recovery={controller.recovery}
        onRetry={controller.retryCommand}
      />
    </>
  );
}

/** Legacy-compatible test adapter; production form 12 uses WorkPolicyApprovalRequestForm. */
export function WorkPolicyApplicationForm({
  startDate,
  endDate,
  controller,
  api = workPolicyApplicationApi
}: WorkPolicyApplicationFormProps) {
  const model = useWorkPolicyApplicationFormModel({
    startDate,
    endDate,
    controller,
    api
  });
  const dailyDate =
    controller.draft?.workType === "flexible" &&
    controller.draft.flexibleMode === "DAILY_DATE";
  const rangeBounds = dailyDate
    ? calendarMonthRangeInputBounds(startDate.value, endDate.value)
    : { startMin: undefined, endMax: undefined };
  return (
    <Section aria-labelledby="work-policy-application-title">
      <h2 id="work-policy-application-title">근무제 적용 신청</h2>
      <EligiblePolicySelect
        status={model.eligibleStatus}
        policies={model.policies}
        selectedPolicyId={controller.draft?.workPolicyId ?? null}
        onSelect={model.selectPolicy}
        onRetry={model.retryEligible}
      />
      <DateFields>
        <label htmlFor="work-policy-startDate">
          신청 시작일
          <input
            id="work-policy-startDate"
            type="date"
            value={startDate.value}
            min={rangeBounds.startMin}
            disabled={!controller.draft}
            aria-invalid={controller.validationIssues.some(
              issue => issue.path === "startDate"
            )}
            onChange={event =>
              model.updateDate("startDate", event.currentTarget.value)
            }
          />
        </label>
        <label htmlFor="work-policy-endDate">
          신청 종료일
          <input
            id="work-policy-endDate"
            type="date"
            value={endDate.value}
            min={startDate.value || undefined}
            max={rangeBounds.endMax}
            disabled={!controller.draft}
            aria-invalid={controller.validationIssues.some(
              issue => issue.path === "endDate"
            )}
            onChange={event =>
              model.updateDate("endDate", event.currentTarget.value)
            }
          />
        </label>
      </DateFields>
      <WorkPolicyApplicationDetail model={model} />
    </Section>
  );
}

export default WorkPolicyApplicationForm;
